import { DIRECT_LIMIT, MailChangeResult, type MailModifyOp } from "@kwp/contracts";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { errorMessage, useTRPC, useTRPCClient } from "../../lib/api";
import { useToast } from "../../ui/toast";
import { newRequestId } from "../tasks/use-task-actions";
import { scopedMessageIds, type Thread, type ThreadPatch } from "./model";
import { snoozeLabel } from "./snooze-model";

/**
 * Inbox mutations (architecture §9): every change is `mail.modify`, `mail.trash`, `mail.snooze`
 * or `mail.unsnooze` with a fresh request id. The list changes at once (an optimistic patch) and the listener then shows what the
 * api wrote to the cache; on failure the patch is dropped and an error toast says why. Changes
 * that move a thread out of view offer Undo, which calls `audit.undo` with the audit id.
 */
export type MailAction =
  | { kind: "archive" }
  | { kind: "trash" }
  | { kind: "spam" }
  | { kind: "star" }
  | { kind: "unstar" }
  | { kind: "read" }
  | { kind: "unread" }
  | { kind: "addTags"; tags: string[] }
  | { kind: "moveToFolder"; folder: string }
  | { kind: "snooze"; until: Date }
  | { kind: "unsnooze" }
  | { kind: "unarchive" }
  | { kind: "notSpam" }
  | { kind: "untrash" };

/** What an action needs of a thread: the list row, or a thread opened from search results. */
export type ActTarget = Pick<Thread, "id" | "accountId" | "messageIds" | "tags">;

const DONE: Partial<Record<MailAction["kind"], string>> = {
  archive: "Archived.",
  trash: "Moved to trash.",
  spam: "Reported as spam.",
  addTags: "Tagged.",
  moveToFolder: "Moved.",
  unsnooze: "Back in the inbox.",
  unarchive: "Moved to the inbox.",
  notSpam: "Not spam: moved to the inbox.",
  untrash: "Restored to the inbox.",
};

export function patchFor(action: MailAction, thread: ActTarget): ThreadPatch {
  switch (action.kind) {
    case "archive":
    case "trash":
    case "spam":
    case "moveToFolder":
    case "snooze":
    case "unsnooze":
    case "unarchive":
    case "notSpam":
    case "untrash":
      return { hidden: true };
    case "star":
      return { starred: true };
    case "unstar":
      return { starred: false };
    case "read":
      return { unread: false };
    case "unread":
      return { unread: true };
    case "addTags":
      return { tags: [...new Set([...thread.tags, ...action.tags])] };
  }
}

export function useMailActions() {
  const trpc = useTRPC();
  const client = useTRPCClient();
  const toast = useToast();
  const [patches, setPatches] = useState<Map<string, ThreadPatch>>(() => new Map());
  const modify = useMutation(trpc.mail.modify.mutationOptions());
  const trash = useMutation(trpc.mail.trash.mutationOptions());
  const snooze = useMutation(trpc.mail.snooze.mutationOptions());
  const unsnooze = useMutation(trpc.mail.unsnooze.mutationOptions());

  const setPatch = useCallback((id: string, patch: ThreadPatch | null) => {
    setPatches((all) => {
      const next = new Map(all);
      if (patch === null) next.delete(id);
      else next.set(id, { ...next.get(id), ...patch });
      return next;
    });
  }, []);

  const undo = useCallback(
    async (auditId: string) => {
      try {
        const out = await client.audit.undo.mutate({ auditId });
        toast({ message: out.skipped > 0 ? `Undone, except ${out.skipped} changed since.` : "Undone." });
      } catch (e) {
        toast({ tone: "error", message: errorMessage(e) });
      }
    },
    [client, toast],
  );

  const act = useCallback(
    async (thread: ActTarget, action: MailAction, options: { stays?: boolean } = {}): Promise<boolean> => {
      const messageIds = scopedMessageIds(thread).slice(-DIRECT_LIMIT);
      // `stays`: the view keeps the thread after this change (snoozing again in the Snoozed view),
      // so there is nothing to hide optimistically; the listener brings the new state.
      if (options.stays !== true) setPatch(thread.id, patchFor(action, thread));
      try {
        const requestId = newRequestId();
        const outcome =
          action.kind === "trash"
            ? await trash.mutateAsync({ requestId, input: { messageIds } })
            : action.kind === "snooze"
              ? await snooze.mutateAsync({ requestId, input: { ids: messageIds, until: action.until } })
              : action.kind === "unsnooze"
                ? await unsnooze.mutateAsync({ requestId, input: { ids: messageIds } })
                : await modify.mutateAsync({ requestId, input: { messageIds, op: action as MailModifyOp } });
        if (outcome.status === "awaiting_approval") {
          setPatch(thread.id, null);
          toast({ message: "This change needs your approval.", action: { label: "Review in Approvals", href: "/approvals" } });
          return false;
        }
        const result = MailChangeResult.safeParse(outcome.result);
        // Unsnooze reports the thread's messages the snooze never held as `not_snoozed`: only a
        // wake-up that brought nothing back is a failure.
        const refused = result.success ? result.data.failed.filter((f) => action.kind !== "unsnooze" || f.reason !== "not_snoozed") : [];
        if (action.kind === "unsnooze" && refused.length === 0 && outcome.auditId === null) {
          setPatch(thread.id, null);
          toast({ tone: "error", message: "This conversation is not snoozed any more." });
          return false;
        }
        if (refused.length > 0) {
          setPatch(thread.id, null);
          toast({ tone: "error", message: `The provider refused ${refused.length} message(s): ${refused[0]?.reason}` });
          return false;
        }
        const message = action.kind === "snooze" ? `Snoozed until ${snoozeLabel(action.until)}.` : DONE[action.kind];
        const auditId = outcome.auditId;
        if (message !== undefined) {
          toast(auditId === null ? { message } : { message, action: { label: "Undo", onClick: () => void undo(auditId) } });
        }
        return true;
      } catch (e) {
        setPatch(thread.id, null);
        toast({ tone: "error", message: errorMessage(e) });
        return false;
      }
    },
    [modify, trash, snooze, unsnooze, setPatch, toast, undo],
  );

  /** Drops patches the listener has caught up with (the thread left the list, or matches). */
  const settle = useCallback((threads: readonly Thread[]) => {
    setPatches((all) => {
      if (all.size === 0) return all;
      const next = new Map(all);
      for (const [id, p] of all) {
        const t = threads.find((x) => x.id === id);
        const caughtUp =
          p.hidden === true
            ? t === undefined
            : t !== undefined &&
              (p.unread === undefined || t.unread === p.unread) &&
              (p.starred === undefined || t.starred === p.starred) &&
              (p.tags === undefined || p.tags.every((tag) => t.tags.includes(tag)));
        if (caughtUp) next.delete(id);
      }
      return next.size === all.size ? all : next;
    });
  }, []);

  /**
   * Another view: its patches are for rows of the last one. A conversation archived from the inbox
   * and hidden there would otherwise stay hidden in Archived until the inbox caught up.
   */
  const reset = useCallback(() => setPatches((all) => (all.size === 0 ? all : new Map())), []);

  return useMemo(() => ({ act, patches, settle, undo, reset }), [act, patches, settle, undo, reset]);
}
